import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'src/hooks/useSnackbar';
import { cancelOrder } from 'src/lib/orders';
import { snackbarErrorData } from 'src/lib/util/snackbarErrorData';
import { container } from 'src/StaticContainer';
import { v4 } from 'uuid';

const log = container.get('Logger').getSubLogger({ name: 'order-blotter-actions' }, { component: 'trading' });

/** Shared order-cancellation action used by both Blotter grid renderers. */
export function useCancelBlotterOrder(): (orderId: string) => void {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { mutate } = useMutation({
        mutationFn: cancelOrder,
        onSuccess: (_data, variables) => {
            enqueueSnackbar(
                t('widgets::order-blotter::actions::cancel-order-success'),
                { variant: 'success' },
                { requestId: variables.requestId },
            );
            log.info({ message: 'Order cancellation succeeded.', requestId: variables.requestId });
        },
        onError: (error) => {
            const { options, responseData } = snackbarErrorData(error);
            enqueueSnackbar(t('widgets::order-blotter::actions::cancel-order-error'), options, responseData);
            log.error({ message: 'Order cancellation failed. Check network response.' });
        },
    });

    return useCallback(
        (orderId: string) => {
            mutate({ orderId, requestId: v4() });
        },
        [mutate],
    );
}
